const DEFAULT_BUFFER_SIZE = 100;
const LEVELS = ['log', 'info', 'warn', 'error', 'debug'];

/**
 * Intercepts the native console methods and stores recent output in a circular ring buffer.
 */
export default class ConsoleCapture {
    static #buffer = [];
    static #bufferSize = DEFAULT_BUFFER_SIZE;
    static #originals = {};
    static #installed = false;

    static get buffer() {
        return this.#buffer;
    }

    static get bufferSize() {
        return this.#bufferSize;
    }

    static setBufferSize(size) {
        this.#bufferSize = size;
        this.#trimBuffer();
    }

    static getSnapshot() {
        return [...this.#buffer];
    }

    static getSnapshotText() {
        return this.#buffer
            .map(({ level, time, message }) => `[${time}] [${level.toUpperCase()}] ${message}`)
            .join('\n');
    }

    static clear() {
        this.#buffer = [];
    }

    static enable() {
        if (this.#installed) {
            return;
        }

        const serialize = (arg) => {
            if (arg === null) return 'null';
            if (arg === undefined) return 'undefined';
            if (typeof arg === 'string') return arg;
            try {
                return JSON.stringify(arg);
            } catch {
                return String(arg);
            }
        };

        for (const level of LEVELS) {
            if (typeof console[level] === 'function') {
                this.#originals[level] = console[level];
                console[level] = (...args) => {
                    this.#buffer.push({
                        level,
                        time: new Date().toISOString(),
                        message: args.map(serialize).join(' '),
                    });

                    this.#trimBuffer();

                    this.#originals[level].apply(console, args);
                };
            }
        }

        this.#installed = true;
    }

    static disable() {
        if (!this.#installed) {
            return;
        }

        for (const level of LEVELS) {
            if (this.#originals[level]) {
                console[level] = this.#originals[level];
            }
        }

        this.#originals = {};
        this.#installed = false;
    }

    static #trimBuffer() {
        while (this.#buffer.length > this.#bufferSize) {
            this.#buffer.shift();
        }
    }
}
